/**
 * "FPA now follows Sage's default X; the pin on Y was released" — shown once per release: dismissing
 * it is remembered in this browser (fpa-backend ADR-0005, `Runway.budget_pin_release`).
 */
import { useCallback, useState } from 'react'

import type { BudgetPinRelease } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { budgetSecondary } from '@/lib/budgetLabel'
import { formatDate } from '@/lib/format'

import styles from './BudgetReleaseNotice.module.css'

const STORAGE_KEY = 'fpa.pinRelease.dismissed'

function dismissedId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function remember(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // Storage blocked: the notice comes back on reload, which is harmless.
  }
}

const REASON: Record<BudgetPinRelease['reason'], string> = {
  sage_default_changed: 'Sage made a different budget its default',
  budget_inactive: 'Sage marked the pinned budget inactive',
  budget_deleted: 'the pinned budget no longer exists in Sage',
}

/** The Sage budget ID (what finance knows), with a meaningful description in parentheses. */
const name = (id: string | null, description: string | null) => {
  if (!id) return 'its default budget'
  const secondary = budgetSecondary({ id, description })
  return secondary ? `${id} (${secondary})` : id
}

export function BudgetReleaseNotice({ release }: { release: BudgetPinRelease | null }) {
  const [hidden, setHidden] = useState<string | null>(() => dismissedId())
  const dismiss = useCallback(() => {
    if (!release) return
    remember(release.id)
    setHidden(release.id)
  }, [release])

  if (!release || hidden === release.id) return null
  return (
    <div className={styles.notice} role="status">
      <ICONS.refresh size={15} className={styles.icon} />
      <div className={styles.text}>
        <b>FPA now follows Sage&rsquo;s default {name(release.now_following, release.now_following_description)}</b>;
        the pin on {name(release.budget_id, release.budget_description)} was released on{' '}
        {formatDate(release.released_at)} because {REASON[release.reason]}.
      </div>
      <button type="button" className={styles.dismiss} onClick={dismiss} aria-label="Dismiss: got it">
        Got it
      </button>
    </div>
  )
}
